import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useToast } from '../components/Toast';
import axios from 'axios';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const toast = useToast();

  // Pre-fill email from URL parameter (passed from Login page)
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      setEmail(decodeURIComponent(emailFromUrl));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Vennligst skriv inn din e-postadresse');
      return;
    }

    setLoading(true);
    
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      // Normalize email: lowercase and trim whitespace
      const normalizedEmail = email.toLowerCase().trim();
      
      // Send email in request body
      await axios.post(`${API_BASE}/api/auth/forgot-password`, {
        email: normalizedEmail
      });
      
      setEmailSent(true);
      toast.success('Nytt passord sendt! Sjekk din innboks.', 6000);
      
    } catch (error) {
      console.error('Password reset request error:', error);
      
      // Check if rate limit exceeded (429 error)
      if (error.response?.status === 429) {
        setRateLimited(true);
        toast.error('Du har nådd maks antall forsøk for i dag. For å beskytte din konto, vennligst prøv igjen i morgen.', 8000);
        setLoading(false);
        return;
      }
      
      // For all other errors, show success message for security (don't reveal if email exists)
      setEmailSent(true);
      toast.success('Nytt passord sendt! Sjekk din innboks.', 6000);
    } finally {
      setLoading(false);
    }
  };

  if (rateLimited) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-white text-2xl font-bold">!</span>
            </div>
            <h2 className="text-3xl font-bold text-white">
              For mange forsøk
            </h2>
            <p className="mt-2 text-gray-400">
              Din konto er midlertidig beskyttet
            </p>
          </div>

          {/* Rate Limit Message */}
          <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="h-6 w-6 text-red-400" />
              <h3 className="text-lg font-semibold text-red-400">
                Maks antall forsøk nådd
              </h3>
            </div>
            <div className="space-y-3 text-red-300 text-sm">
              <p>
                Du har forsøkt å resette passordet for <strong>{email}</strong> 3 ganger i dag.
              </p>
              <p>
                For å beskytte din konto mot uautorisert tilgang, har vi midlertidig begrenset passord-reset funksjonen.
              </p>
              <p className="text-red-400 font-medium">
                Vennligst prøv igjen i morgen, eller kontakt din skyteleder hvis du trenger umiddelbar hjelp.
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-6">
            <h4 className="text-white font-medium mb-3">Hva kan du gjøre:</h4>
            <ul className="space-y-2 text-gray-300 text-sm list-disc list-inside">
              <li>Vent til i morgen og prøv igjen</li>
              <li>Kontakt din skyteleder for å få hjelp</li>
              <li>Sjekk om du har mottatt tidligere epost med midlertidig passord</li>
              <li>Forsikre deg om at du bruker riktig epostadresse</li>
            </ul>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tilbake til innlogging
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-gray-900 text-2xl font-bold">
                A<span className="text-sm">L</span>
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white">
              E-post sendt!
            </h2>
            <p className="mt-2 text-gray-400">
              Sjekk din innboks for videre instruksjoner
            </p>
          </div>

          {/* Success Message */}
          <div className="bg-green-900/20 border border-green-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-green-400">
                Nytt passord sendt!
              </h3>
            </div>
            <div className="space-y-3 text-green-300 text-sm">
              <p>
                Vi har sendt et nytt midlertidig passord til <strong>{email}</strong>
              </p>
              <p>
                Eposten inneholder ditt nye passord som du kan bruke for å logge inn.
              </p>
              <p className="text-green-400 font-medium">
                Hvis du ikke ba om passord-reset, kontakt din skyteleder umiddelbart.
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-6">
            <h4 className="text-white font-medium mb-3">Neste steg:</h4>
            <ol className="space-y-2 text-gray-300 text-sm list-decimal list-inside">
              <li>Åpne eposten vi nettopp sendte deg</li>
              <li>Kopier det nye midlertidige passordet</li>
              <li>Logg inn med det nye passordet</li>
              <li>Gå til "Profil" for å sette ditt eget sikre passord</li>
            </ol>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tilbake til innlogging
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
            <span className="text-gray-900 text-2xl font-bold">
              A<span className="text-sm">L</span>
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white">
            Glemt passord?
          </h2>
          <p className="mt-2 text-gray-400">
            Skriv inn din e-postadresse så sender vi deg et nytt midlertidig passord
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Din e-postadresse
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-10 py-3 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                placeholder="din@epost.no"
              />
            </div>
          </div>

          <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-4">
            <div className="text-blue-300 text-sm space-y-2">
              <p className="font-medium">Hva skjer når du klikker "Send nytt passord":</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Du får en epost med et nytt midlertidig passord</li>
                <li>Logg inn med det nye passordet</li>
                <li>Gå til "Profil" for å sette ditt eget sikre passord</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4">
            <div className="text-yellow-300 text-sm">
              <p className="font-medium mb-1">⚠️ Sikkerhetsbegrensning</p>
              <p>Av sikkerhetshensyn kan du maks resette passordet 3 ganger per dag.</p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !email}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail className="h-5 w-5 mr-2" />
              {loading ? 'Sender...' : 'Send nytt passord'}
            </button>
          </div>

          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tilbake til innlogging
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;